// Copyright (c) Microsoft. All rights reserved.

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Constants } from '../../../Constants';
import { AgentOverride, AgentOverrides } from '../../../libs/models/AgentConfig';
import { FeedbackOpenConfig, ServiceInfo } from '../../../libs/models/ServiceInfo';
import { TokenUsage, TokenUsageFunctionNameMap } from '../../../libs/models/TokenUsage';
import { ActiveUserInfo, Alert, AppState, FeedbackContext, initialState } from './AppState';
import { AppState as AppStateEnum } from '../../../AppState';
import { AiProviderSelection } from '../../../libs/models/AiProvider';
import { UserPreferences } from '../../../libs/models/UserPreferences';
import { UserMemory } from '../../../libs/models/UserMemory';

export const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        setAppState: (state: AppState, action: PayloadAction<AppStateEnum>) => {
            state.currentState = action.payload;
        },
        setAlerts: (state: AppState, action: PayloadAction<Alert[]>) => {
            state.alerts = action.payload;
        },
        addAlert: (state: AppState, action: PayloadAction<Alert>) => {
            if (
                action.payload.id == Constants.app.CONNECTION_ALERT_ID ||
                isServerConnectionError(action.payload.message)
            ) {
                updateConnectionStatus(state, action.payload);
            } else {
                addNewAlert(state.alerts, action.payload);
            }
        },
        removeAlert: (state: AppState, action: PayloadAction<number>) => {
            state.alerts.splice(action.payload, 1);
        },
        removeAlertWithId: (state: AppState, action: PayloadAction<string>) => {
            state.alerts = state.alerts.filter((alert) => alert.id !== action.payload);
        },
        setActiveUserInfo: (state: AppState, action: PayloadAction<ActiveUserInfo>) => {
            state.activeUserInfo = action.payload;
        },
        updateTokenUsage: (state: AppState, action: PayloadAction<TokenUsage>) => {
            Object.keys(TokenUsageFunctionNameMap).forEach((key) => {
                action.payload[key] = getTotalTokenUsage(state.tokenUsage[key], action.payload[key]);
            });
            state.tokenUsage = action.payload;
        },
        setServiceInfo: (state: AppState, action: PayloadAction<ServiceInfo>) => {
            state.serviceInfo = action.payload;

            if (typeof action.payload.featureFlags === 'object') {
                const featureFlagsUnknown = action.payload.featureFlags as Record<string, unknown>;
                const featureFlagsBool: Record<string, boolean> = {};

                for (const key in featureFlagsUnknown) {
                    if (typeof featureFlagsUnknown[key] !== 'boolean') {
                        throw new Error(`Invalid type for featureFlags[${key}], expected boolean`);
                    }
                    featureFlagsBool[key] = featureFlagsUnknown[key];
                }

                // Create a proxy on featureFlags so that we can return a default false for any feature that doesn't exist
                // in the API reply.
                const featureFlags: Record<string, boolean> = new Proxy<Record<string, boolean>>(featureFlagsBool, {
                    get: function (target, name) {
                        return target.hasOwnProperty(name) ? target[name.toString()] : false;
                    },
                });
                state.serviceInfo.featureFlags = featureFlags;
            }
        },
        setEmbeddedMode: (state: AppState, action: PayloadAction<boolean>) => {
            state.embeddedMode = action.payload;
        },
        setFeedbackIsOpen: (state: AppState, action: PayloadAction<FeedbackOpenConfig>) => {
            // If we're closing, let's reset the form
            if (!action.payload.isOpen) {
                const hideRating = state.feedbackForm.hideRating;
                state.feedbackForm = { ...initialState.feedbackForm, hideRating };
                return;
            }

            const isPositive = state.feedbackForm.isPositive;
            const hideRating = action.payload.hideRating ?? state.feedbackForm.hideRating;

            state.feedbackForm = { ...state.feedbackForm, isOpen: action.payload.isOpen, isPositive, hideRating };
        },
        setFeedbackIsSending: (state: AppState, action: PayloadAction<boolean>) => {
            state.feedbackForm = { ...state.feedbackForm, isSending: action.payload };
        },
        setFeedbackValue: (state: AppState, action: PayloadAction<string>) => {
            state.feedbackForm = { ...state.feedbackForm, value: action.payload };
        },
        setFeedbackIsPositive: (state: AppState, action: PayloadAction<boolean | undefined>) => {
            state.feedbackForm = { ...state.feedbackForm, isPositive: action.payload };
        },
        setFeedbackContext: (state: AppState, action: PayloadAction<FeedbackContext>) => {
            state.feedbackForm = {
                ...state.feedbackForm,
                context: { ...state.feedbackForm.context, ...action.payload },
            };
        },
        setFeedbackRating: (state: AppState, action: PayloadAction<string | null>) => {
            state.feedbackForm = { ...state.feedbackForm, rating: action.payload };
        },
        setRateLimit: (state: AppState, action: PayloadAction<{ isLimited: boolean; resetTime?: number }>) => {
            state.isRateLimited = action.payload.isLimited;
            state.rateLimitResetTime = action.payload.resetTime ?? null;
        },
        setShowCsat: (state: AppState, action: PayloadAction<boolean>) => {
            state.showCsat = action.payload;
        },
        setAuthToken: (state: AppState, action: PayloadAction<string | null>) => {
            state.authToken = action.payload;
        },
        setAiProviderSelection: (state: AppState, action: PayloadAction<AiProviderSelection>) => {
            state.aiProviderSelection = action.payload;
            // Persist to localStorage
            try {
                if (Object.keys(action.payload).length > 0) {
                    localStorage.setItem('aiProviderSelection', JSON.stringify(action.payload));
                } else {
                    localStorage.removeItem('aiProviderSelection');
                }
            } catch {
                // Ignore localStorage errors
            }
        },
        setAgentOverrides: (state: AppState, action: PayloadAction<AgentOverrides>) => {
            state.agentOverrides = action.payload;
            // Persist to localStorage
            try {
                localStorage.setItem('agentOverrides', JSON.stringify(action.payload));
            } catch {
                // Ignore localStorage errors
            }
        },
        setAgentOverride: (
            state: AppState,
            action: PayloadAction<{ agentName: string; override: AgentOverride | undefined }>,
        ) => {
            const { agentName, override } = action.payload;
            if (override) {
                state.agentOverrides[agentName] = override;
            } else {
                // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
                delete state.agentOverrides[agentName];
            }
            // Persist to localStorage
            try {
                localStorage.setItem('agentOverrides', JSON.stringify(state.agentOverrides));
            } catch {
                // Ignore localStorage errors
            }
        },
        clearAgentOverrides: (state: AppState) => {
            state.agentOverrides = {};
            try {
                localStorage.removeItem('agentOverrides');
            } catch {
                // Ignore localStorage errors
            }
        },
        setUserPreferences: (state: AppState, action: PayloadAction<UserPreferences>) => {
            state.userPreferences = action.payload;
        },
        setUserMemory: (state: AppState, action: PayloadAction<UserMemory>) => {
            state.userMemory = action.payload;
        },
    },
});

export const {
    addAlert,
    removeAlert,
    removeAlertWithId,
    setAlerts,
    setActiveUserInfo,
    updateTokenUsage,
    setServiceInfo,
    setEmbeddedMode,
    setFeedbackIsOpen,
    setFeedbackIsSending,
    setFeedbackValue,
    setFeedbackIsPositive,
    setFeedbackContext,
    setFeedbackRating,
    setRateLimit,
    setShowCsat,
    setAuthToken,
    setAppState,
    setAiProviderSelection,
    setAgentOverrides,
    setAgentOverride,
    clearAgentOverrides,
    setUserPreferences,
    setUserMemory,
} = appSlice.actions;

export default appSlice.reducer;

const getTotalTokenUsage = (previousSum?: number, current?: number) => {
    if (previousSum === undefined) {
        return current;
    }
    if (current === undefined) {
        return previousSum;
    }

    return previousSum + current;
};

const isServerConnectionError = (message: string) => {
    return (
        message.includes(`Cannot send data if the connection is not in the 'Connected' State.`) ||
        message.includes(`Server timeout elapsed without receiving a message from the server.`)
    );
};

const addNewAlert = (alerts: Alert[], newAlert: Alert) => {
    if (alerts.length === 3) {
        alerts.shift();
    }

    alerts.push(newAlert);
};

const updateConnectionStatus = (state: AppState, statusUpdate: Alert) => {
    if (isServerConnectionError(statusUpdate.message)) {
        statusUpdate.message =
            // Constant message so alert UI doesn't feel glitchy on every connection error from SignalR
            'Cannot send data due to lost connection or server timeout. Try refreshing this page to restart the connection.';
    }

    // There should only ever be one connection alert at a time,
    // so we tag the alert with a unique ID so we can remove if needed
    statusUpdate.id ??= Constants.app.CONNECTION_ALERT_ID;

    // Remove the existing connection alert if it exists
    const connectionAlertIndex = state.alerts.findIndex((alert) => alert.id === Constants.app.CONNECTION_ALERT_ID);
    if (connectionAlertIndex !== -1) {
        state.alerts.splice(connectionAlertIndex, 1);
    }

    addNewAlert(state.alerts, statusUpdate);
};
